/**
 * The shared bottom-layer Bot Framework REST client (spec §4.1) — written once
 * here, reused by the later agent-side tool-face step. Raw fetch only; no
 * botbuilder.
 */

/** Entra token endpoint base for the Bot Framework's shared tenant. */
const DEFAULT_LOGIN_BASE_URL = "https://login.microsoftonline.com/botframework.com";

/** OAuth scope every connector-service call is authorized under. */
const BOTFRAMEWORK_SCOPE = "https://api.botframework.com/.default";

/** Refresh the cached token this long before its actual expiry. */
const TOKEN_EXPIRY_MARGIN_MS = 60_000;

/** Max attempts for one connector-service call (initial + retries on 429). */
const MAX_TRIES = 3;

/** Options for {@link MsTeamsClient}. */
export type MsTeamsClientOptions = {
  appId: string;
  clientSecret: string;
  /** Default "https://login.microsoftonline.com/botframework.com" — injectable for mocks. */
  loginBaseUrl?: string;
  fetchImpl?: typeof fetch;
  now?: () => number;
};

/** A failed connector-service call, with the HTTP status for callers to branch on. */
export class MsTeamsApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "MsTeamsApiError";
  }
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Minimal Bot Framework connector-service client: client_credentials token
 * acquisition (cached) plus activity post/update against a channel-provided
 * serviceUrl. Honors 429 Retry-After with jittered backoff, max 3 tries.
 */
export class MsTeamsClient {
  private readonly opts: MsTeamsClientOptions;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;
  private token?: { value: string; expiresAt: number };

  constructor(opts: MsTeamsClientOptions) {
    this.opts = opts;
    this.fetchImpl = opts.fetchImpl ?? fetch;
    this.now = opts.now ?? Date.now;
  }

  /** client_credentials token for scope https://api.botframework.com/.default; cached until exp-60s. */
  async getToken(): Promise<string> {
    if (this.token && this.token.expiresAt > this.now()) return this.token.value;
    const loginBaseUrl = this.opts.loginBaseUrl ?? DEFAULT_LOGIN_BASE_URL;
    const res = await this.fetchImpl(`${loginBaseUrl}/oauth2/v2.0/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "client_credentials",
        client_id: this.opts.appId,
        client_secret: this.opts.clientSecret,
        scope: BOTFRAMEWORK_SCOPE,
      }).toString(),
    });
    if (!res.ok) throw new MsTeamsApiError(res.status, `token request failed: ${res.status}`);
    const body = (await res.json()) as { access_token: string; expires_in: number };
    this.token = {
      value: body.access_token,
      expiresAt: this.now() + body.expires_in * 1000 - TOKEN_EXPIRY_MARGIN_MS,
    };
    return body.access_token;
  }

  /** POST {serviceUrl}/v3/conversations/{conversationId}/activities[/{replyToId}] → { id }. */
  async postActivity(
    serviceUrl: string,
    conversationId: string,
    activity: Record<string, unknown>,
    replyToId?: string,
  ): Promise<{ id: string }> {
    const suffix = replyToId ? `/${encodeURIComponent(replyToId)}` : "";
    const res = await this.request(
      "POST",
      `${this.activitiesUrl(serviceUrl, conversationId)}${suffix}`,
      activity,
    );
    const body = (await res.json().catch(() => ({}))) as { id?: string };
    return { id: body.id ?? "" };
  }

  /** PUT {serviceUrl}/v3/conversations/{conversationId}/activities/{activityId}. Same retry policy. */
  async updateActivity(
    serviceUrl: string,
    conversationId: string,
    activityId: string,
    activity: Record<string, unknown>,
  ): Promise<void> {
    const url = `${this.activitiesUrl(serviceUrl, conversationId)}/${encodeURIComponent(activityId)}`;
    await this.request("PUT", url, activity);
  }

  private activitiesUrl(serviceUrl: string, conversationId: string): string {
    const base = serviceUrl.replace(/\/+$/, "");
    return `${base}/v3/conversations/${encodeURIComponent(conversationId)}/activities`;
  }

  /**
   * Authorized JSON request honoring 429 Retry-After with jittered backoff,
   * max 3 tries. One 401 forces a fresh token and a single immediate retry.
   */
  private async request(
    method: string,
    url: string,
    body: Record<string, unknown>,
  ): Promise<Response> {
    let retried401 = false;
    for (let attempt = 1; ; attempt++) {
      const token = await this.getToken();
      const res = await this.fetchImpl(url, {
        method,
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) return res;
      if (res.status === 401 && !retried401 && attempt < MAX_TRIES) {
        // The cached token was revoked or expired early — re-acquire once.
        retried401 = true;
        this.token = undefined;
        continue;
      }
      if (res.status !== 429 || attempt >= MAX_TRIES) {
        throw new MsTeamsApiError(res.status, `${method} ${url} failed: ${res.status}`);
      }
      const retryAfterSec = Number(res.headers.get("retry-after"));
      const baseDelayMs =
        Number.isFinite(retryAfterSec) && retryAfterSec > 0 ? retryAfterSec * 1000 : 1000 * attempt;
      await sleep(baseDelayMs + Math.random() * 250);
    }
  }
}
